import {isBefore} from 'date-fns';
import {closeCurrentDialog, openDialog} from 'platform/components';
import {ThemeColorPath} from 'platform/foundation';
import {match} from 'ts-pattern';

import {CodeResponseBody} from '@dms/api/core';
import {
  WorkshopActivityRelatedEmailResponseBody,
  WorkshopActivityRelatedEmailResponseBodySchema,
  WorkshopActivityRelatedSmsResponseBody,
  WorkshopActivityRelatedSmsResponseBodySchema,
  WorkshopActivityRelatedTaskResponseBody,
  WorkshopActivityRelatedTaskResponseBodySchema,
  WorkshopInterestActivityResponseBody,
  WorkshopInterestActivityType,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, SentMessagePreviewDialog} from '@dms/shared';

import {buildArray, isNotNilOrEmpty, parseDate, valueOrPlaceholder} from 'shared/web';

import {MessageDialog} from '../components/ActivityTab/components/MessageDialog';
import {LogContactDialog} from '../components/LogContactDialog/LogContactDialog';
import {IconColors} from '../constants/activityItem';
import {TransformedActivityItem, ActivityItemDataLink} from '../types/ActivitySidebarAction';
import {getTaskIcon} from './getTaskIcon';

const getTaskActivityColor = (
  task: WorkshopActivityRelatedTaskResponseBody | null
): ThemeColorPath => {
  const isFinished = task?.finishedAt !== null;
  const taskDueDate = task?.dueTo ?? task?.dueDate;
  const isOverdue = taskDueDate ? isBefore(parseDate(taskDueDate), new Date()) : false;

  return !isFinished && isOverdue ? IconColors.RED : IconColors.GREEN;
};

export type WorkshopActivityListItemType = WorkshopInterestActivityType | 'TASK_CREATED';

export type WorkshopActivityListItem = Omit<
  WorkshopInterestActivityResponseBody,
  'activityType'
> & {
  activityType: WorkshopActivityListItemType;
};

const getRelatedEmail = (
  item: WorkshopActivityListItem
): WorkshopActivityRelatedEmailResponseBody | null => {
  if (item.relatedResourceId !== 'EMAIL' || item.relatedRecord === null) {
    return null;
  }
  const parsed = WorkshopActivityRelatedEmailResponseBodySchema.safeParse(item.relatedRecord);
  return parsed.success ? parsed.data : null;
};

const getRelatedSms = (
  item: WorkshopActivityListItem
): WorkshopActivityRelatedSmsResponseBody | null => {
  if (item.relatedResourceId !== 'SMS' || item.relatedRecord === null) {
    return null;
  }
  const parsed = WorkshopActivityRelatedSmsResponseBodySchema.safeParse(item.relatedRecord);
  return parsed.success ? parsed.data : null;
};

const getRelatedTask = (
  item: WorkshopActivityListItem
): WorkshopActivityRelatedTaskResponseBody | null => {
  if (item.relatedResourceId !== 'TASK' || item.relatedRecord === null) {
    return null;
  }
  const parsed = WorkshopActivityRelatedTaskResponseBodySchema.safeParse(item.relatedRecord);
  return parsed.success ? parsed.data : null;
};

const handleOpenLogContactDialog = (workshopInterestId: string) => {
  openDialog(<LogContactDialog workshopInterestId={workshopInterestId} />, {
    id: 'LOG_CONTACT_DIALOG',
    title: i18n.t('entity.activityPanel.logContact.editLog'),
    scrollBehavior: 'outside',
  });
};

const handleOpenSmsDialog = (body: string) => {
  openDialog(<MessageDialog message={body} />, {
    id: 'MESSAGE_DIALOG_SMS',
    title: i18n.t('entity.activityPanel.activity.actions.smsSent'),
    buttons: [
      {
        variant: 'secondary',
        title: i18n.t('general.actions.close'),
        onClick: closeCurrentDialog,
      },
    ],
  });
};

const handleOpenEmailPreviewDialog = (
  item: WorkshopActivityListItem,
  email: WorkshopActivityRelatedEmailResponseBody
) => {
  const attachments = email.attachments?.map((attachment) => ({
    id: attachment.uri,
    title: attachment.filename,
    remoteUrl: attachment.uri,
    contentType: attachment.contentType,
  }));

  const sender = email.sender.name ?? EMPTY_PLACEHOLDER;

  openDialog(
    <SentMessagePreviewDialog
      attachments={attachments}
      body={valueOrPlaceholder(email.body)}
      recipient={valueOrPlaceholder(email.sentTo)}
      sender={sender}
      sentAt={item.timestamp}
      subject={valueOrPlaceholder(email.subject)}
    />,
    {
      id: 'MESSAGE_DIALOG_EMAIL',
      title: i18n.t('entity.activityPanel.activity.actions.emailSent'),
      size: 'xLarge',
      buttons: [
        {
          variant: 'secondary',
          title: i18n.t('general.actions.close'),
          onClick: closeCurrentDialog,
        },
      ],
    }
  );
};

export const transformWorkshopActivityItem = (
  item: WorkshopActivityListItem,
  overrides?: {
    source?: CodeResponseBody;
    closeReason?: CodeResponseBody;
    userName?: string;
  },
  openEditTaskFormDialog?: (id: string) => void
): TransformedActivityItem | null =>
  match<WorkshopActivityListItemType, TransformedActivityItem | null>(item.activityType)
    .with('INTEREST_CREATED', () => ({
      id: item.id,
      color: IconColors.BLUE,
      title: i18n.t('entity.activityPanel.activity.interestCreated'),
      interestType: overrides?.source?.name,
      description: item.description,
      userId: item.userId,
      timestamp: item.timestamp,
      icon: 'content/next_week',
    }))
    .with('INTEREST_OPENED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestOpened'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userId: item.userId,
      icon: 'content/next_week',
    }))
    .with('INTEREST_REOPENED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestReopened'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userId: item.userId,
      icon: 'content/next_week',
    }))
    .with('INTEREST_VISIT_SCHEDULED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestVisitScheduled'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userId: item.userId,
      description: item.description,
      icon: 'action/calendar_today',
    }))
    .with('INTEREST_CLOSED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestClosed'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userId: item.userId,
      icon: 'content/next_week',
    }))
    .with('INTEREST_OPPORTUNITY_LOST', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestOpportunityLost'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      userId: item.userId,
      description: overrides?.closeReason?.name ?? item.description,
      icon: 'custom/business_case_sad',
      links: buildArray<ActivityItemDataLink>().when(item.reasonCloseNote, {
        label: i18n.t('general.labels.note'),
        tooltip: [item.reasonCloseNote ?? ''],
      }),
    }))
    .with('EMAIL_TO_CUSTOMER', () => {
      const email = getRelatedEmail(item);

      return {
        id: item.id,
        title: i18n.t('entity.activityPanel.activity.emailToCustomer'),
        timestamp: item.timestamp,
        userName: email?.sentTo ?? undefined,
        interestType: email?.subject ?? undefined,
        color: IconColors.GREEN,
        icon: 'communication/email',
        links: buildArray<ActivityItemDataLink>().when(email, {
          label: i18n.t('general.labels.detail'),
          onClick: () => email && handleOpenEmailPreviewDialog(item, email),
        }),
      };
    })
    .with('SMS_TO_CUSTOMER', () => {
      const sms = getRelatedSms(item);

      return {
        id: item.id,
        title: i18n.t('entity.activityPanel.activity.smsToCustomer'),
        userName: sms?.sentTo ?? undefined,
        timestamp: item.timestamp,
        color: IconColors.GREEN,
        icon: 'communication/textsms',
        links: buildArray<ActivityItemDataLink>().when(sms, {
          label: i18n.t('general.labels.detail'),
          onClick: () => sms && handleOpenSmsDialog(sms.body),
        }),
      };
    })
    .with('CALL_FROM_CUSTOMER', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.callFromCustomer'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      interestType: i18n.t('entity.activityPanel.activity.callFromCustomer'),
      userId: item.userId,
      description: item.description,
      icon: 'notification/phone_inbound',
      onClick: () => handleOpenLogContactDialog(item.interestId),
    }))
    .with('CALL_TO_CUSTOMER', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.callToCustomer'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      interestType: i18n.t('entity.activityPanel.activity.callToCustomer'),
      userId: item.userId,
      description: item.description,
      icon: 'notification/phone_outbound',
      onClick: () => handleOpenLogContactDialog(item.interestId),
    }))
    .with('MESSAGE_FROM_CUSTOMER', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.messageFromCustomer'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      interestType: i18n.t('entity.activityPanel.activity.messageFromCustomer'),
      userId: item.userId,
      description: item.description,
      icon: 'communication/comment',
      onClick: () => handleOpenLogContactDialog(item.interestId),
    }))
    .with('MESSAGE_TO_CUSTOMER', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.messageToCustomer'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      interestType: i18n.t('entity.activityPanel.activity.messageToCustomer'),
      userId: item.userId,
      description: item.description,
      icon: 'communication/comment',
      onClick: () => handleOpenLogContactDialog(item.interestId),
    }))
    .with('CUSTOMER_VISIT', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.customerVisit'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      interestType: i18n.t('entity.activityPanel.activity.customerVisit'),
      userId: item.userId,
      description: item.description,
      icon: 'communication/location_on',
      onClick: () => handleOpenLogContactDialog(item.interestId),
    }))
    .with('TASK_CREATED', 'TASK_FINISHED', () => {
      const task = getRelatedTask(item);

      return {
        id: item.id,
        title: task?.title ?? item.description ?? '',
        timestamp: item.timestamp,
        icon: getTaskIcon(task?.type),
        color: getTaskActivityColor(task),
        userId: item.userId,
        userName: isNotNilOrEmpty(overrides?.userName) ? overrides?.userName : undefined,
        description: task?.description,
        links: buildArray<ActivityItemDataLink>().when(task, {
          label: i18n.t('general.labels.detail'),
          onClick: () => openEditTaskFormDialog && task && openEditTaskFormDialog(task.id),
        }),
      };
    })
    // Backend may ship new activity types before the FE; hide unknown types instead of crashing.
    .otherwise(() => null);
